import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, MapPin, Calendar, Share2, Clock, Star,
  ChevronLeft, ChevronRight, X, Facebook, Twitter, Linkedin, Link2, Play
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Calculate reading time
const calculateReadingTime = (content) => {
  const wordsPerMinute = 200;
  const words = content?.split(/\s+/).length || 0;
  return Math.max(1, Math.ceil(words / wordsPerMinute));
};

// Extract YouTube video ID from URL
const getYouTubeId = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

// Video Embed Component
const VideoEmbed = ({ video }) => {
  if (video.video_type === 'youtube') {
    const youtubeId = getYouTubeId(video.url);
    if (!youtubeId) return null;
    
    return (
      <div className="relative aspect-video bg-black">
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title={video.title || 'Video de YouTube'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }
  
  if (video.video_type === 'cloudinary') {
    return (
      <div className="relative aspect-video bg-black">
        <video
          src={video.url}
          title={video.title || 'Video'}
          controls
          className="w-full h-full object-contain"
          preload="metadata"
        >
          Tu navegador no soporta la reproducción de video.
        </video>
      </div>
    );
  }
  
  return null;
};

// Star Rating Component
const StarRating = ({ rating, onRate, disabled, totalRatings }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => !disabled && onRate(star)}
            onMouseEnter={() => !disabled && setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            disabled={disabled}
            className={`transition-transform ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            data-testid={`star-${star}`}
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                (hoverRating || rating) >= star
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-muted-foreground'
              }`}
            />
          </button>
        ))}
      </div>
      {totalRatings > 0 && (
        <span className="text-xs text-muted-foreground">
          {rating.toFixed(1)} ({totalRatings} {totalRatings === 1 ? 'valoración' : 'valoraciones'})
        </span>
      )}
    </div>
  );
};

// Gallery Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (diff > threshold) onNext();
    else if (diff < -threshold) onPrev();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={onClose}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 text-white/70 hover:text-white transition-colors"
          data-testid="lightbox-close"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Navigation arrows */}
        {currentIndex > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
        )}
        {currentIndex < images.length - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
        )}

        {/* Image */}
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          src={images[currentIndex]}
          alt={`Gallery image ${currentIndex + 1}`}
          className="max-w-[90vw] max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Share Buttons Component
const ShareButtons = ({ url, title }) => {
  const shareUrl = encodeURIComponent(url);
  const shareTitle = encodeURIComponent(title);

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado al portapapeles');
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground mr-2">Compartir:</span>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-muted hover:bg-blue-600 hover:text-white transition-colors"
        data-testid="share-facebook"
      >
        <Facebook className="w-4 h-4" />
      </a>
      <a
        href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-muted hover:bg-sky-500 hover:text-white transition-colors"
        data-testid="share-twitter"
      >
        <Twitter className="w-4 h-4" />
      </a>
      <a
        href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-muted hover:bg-blue-700 hover:text-white transition-colors"
        data-testid="share-linkedin"
      >
        <Linkedin className="w-4 h-4" />
      </a>
      <button
        onClick={copyLink}
        className="p-2 rounded-full bg-muted hover:bg-foreground hover:text-background transition-colors"
        data-testid="share-copy"
      >
        <Link2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Related Posts Component
const RelatedPosts = ({ posts }) => {
  if (!posts || posts.length === 0) return null;

  return (
    <div className="mt-12 pt-8 border-t border-border">
      <h3 className="font-serif text-2xl mb-6">Artículos Relacionados</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.map((post) => (
          <Link
            key={post.post_id}
            to={`/blog/${post.post_id}`}
            className="group block"
            data-testid={`related-post-${post.post_id}`}
          >
            <div className="aspect-video overflow-hidden mb-3">
              <img
                src={post.thumbnail}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <h4 className="font-serif text-lg group-hover:text-primary transition-colors line-clamp-2">
              {post.title}
            </h4>
            <p className="text-sm text-muted-foreground mt-1">{post.date}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

// Post Navigation Component
const PostNavigation = ({ previous, next }) => {
  if (!previous && !next) return null;

  return (
    <div className="mt-12 pt-8 border-t border-border">
      <div className="flex justify-between items-stretch gap-4">
        {previous ? (
          <Link
            to={`/blog/${previous.post_id}`}
            className="group flex-1 flex items-center gap-4 p-4 border border-border hover:border-foreground/30 transition-colors"
            data-testid="nav-previous"
          >
            <ChevronLeft className="w-6 h-6 text-muted-foreground group-hover:text-foreground transition-colors" />
            <div className="flex-1 text-left">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Anterior</span>
              <h4 className="font-serif text-sm md:text-base group-hover:text-primary transition-colors line-clamp-1">
                {previous.title}
              </h4>
            </div>
          </Link>
        ) : <div className="flex-1" />}
        
        {next ? (
          <Link
            to={`/blog/${next.post_id}`}
            className="group flex-1 flex items-center gap-4 p-4 border border-border hover:border-foreground/30 transition-colors"
            data-testid="nav-next"
          >
            <div className="flex-1 text-right">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Siguiente</span>
              <h4 className="font-serif text-sm md:text-base group-hover:text-primary transition-colors line-clamp-1">
                {next.title}
              </h4>
            </div>
            <ChevronRight className="w-6 h-6 text-muted-foreground group-hover:text-foreground transition-colors" />
          </Link>
        ) : <div className="flex-1" />}
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ post, relatedPosts, onRate, userRating, hasRated, ratingBreakdown }) => {
  const avgRating = post.rating_count > 0 ? post.rating_sum / post.rating_count : 0;
  const breakdown = ratingBreakdown || post.rating_breakdown || { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
  const totalVotes = post.rating_count || 0;
  
  return (
    <aside className="lg:w-80 space-y-8">
      {/* Author/Photographer Card */}
      <div className="p-6 bg-card border border-border">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-muted">
            <img 
              src="https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/439uj080_Yo.png" 
              alt="Jon Quintano"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h4 className="font-serif text-lg">Jon Quintano</h4>
            <p className="text-sm text-muted-foreground">Fotógrafo Profesional</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Apasionado por capturar momentos únicos y contar historias a través de mis fotografías.
        </p>
      </div>

      {/* Rating Card */}
      <div className="p-6 bg-card border border-border">
        <h4 className="font-serif text-lg mb-4 text-center">¿Te ha gustado?</h4>
        <div className="text-center mb-4">
          <StarRating
            rating={userRating || avgRating}
            onRate={onRate}
            disabled={hasRated}
            totalRatings={totalVotes}
          />
          {hasRated && (
            <p className="text-xs text-muted-foreground mt-2">¡Gracias por tu valoración!</p>
          )}
        </div>
        
        {/* Rating Breakdown */}
        {totalVotes > 0 && (
          <div className="mt-4 pt-4 border-t border-border space-y-2">
            <p className="text-xs text-muted-foreground mb-3 text-center">
              Desglose de valoraciones
            </p>
            {[5, 4, 3, 2, 1].map((star) => {
              const count = breakdown[star.toString()] || 0;
              const percentage = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-2">
                  <div className="flex items-center gap-1 w-8">
                    <span className="text-xs font-medium">{star}</span>
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Share Card */}
      <div className="p-6 bg-card border border-border">
        <h4 className="font-serif text-lg mb-4">Compartir</h4>
        <ShareButtons url={window.location.href} title={post.title} />
      </div>

      {/* Categories/Tags */}
      <div className="p-6 bg-card border border-border">
        <h4 className="font-serif text-lg mb-4">Categoría</h4>
        <Link 
          to={`/#blog`}
          className="inline-block px-3 py-1 bg-muted text-sm hover:bg-foreground hover:text-background transition-colors"
        >
          {post.category}
        </Link>
      </div>

      {/* Quick Related Posts */}
      {relatedPosts && relatedPosts.length > 0 && (
        <div className="p-6 bg-card border border-border">
          <h4 className="font-serif text-lg mb-4">También te puede interesar</h4>
          <div className="space-y-4">
            {relatedPosts.slice(0, 2).map((related) => (
              <Link
                key={related.post_id}
                to={`/blog/${related.post_id}`}
                className="group flex gap-3"
              >
                <div className="w-16 h-16 flex-shrink-0 overflow-hidden">
                  <img
                    src={related.thumbnail}
                    alt={related.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h5 className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2">
                    {related.title}
                  </h5>
                  <p className="text-xs text-muted-foreground mt-1">{related.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

// Main BlogDetail Component
export const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [navigation, setNavigation] = useState({ previous: null, next: null });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [hasRated, setHasRated] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const { theme } = useTheme();

  // Calculate visible thumbnails based on screen width
  const getVisibleCount = () => {
    if (typeof window === 'undefined') return 4;
    return window.innerWidth < 768 ? 2 : 4;
  };
  
  // Item width for gallery animation (w-32 = 128px on mobile, w-40 = 160px on desktop)
  const galleryItemWidth = typeof window !== 'undefined' && window.innerWidth < 768 ? 128 : 160;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch post
        const postResponse = await fetch(`${API}/blog/${id}`);
        if (postResponse.ok) {
          const postData = await postResponse.json();
          setPost(postData);
        }

        // Fetch related posts
        const relatedResponse = await fetch(`${API}/blog/${id}/related`);
        if (relatedResponse.ok) {
          const relatedData = await relatedResponse.json();
          setRelatedPosts(relatedData);
        }

        // Fetch navigation
        const navResponse = await fetch(`${API}/blog/${id}/navigation`);
        if (navResponse.ok) {
          const navData = await navResponse.json();
          setNavigation(navData);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    window.scrollTo(0, 0);
  }, [id]);

  const handleRate = async (rating) => {
    try {
      const response = await fetch(`${API}/blog/${id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating })
      });

      if (response.ok) {
        const data = await response.json();
        setUserRating(rating);
        setHasRated(true);
        setPost(prev => ({
          ...prev,
          rating_sum: (prev.rating_sum || 0) + rating,
          rating_count: (prev.rating_count || 0) + 1,
          rating_breakdown: data.rating_breakdown || prev.rating_breakdown
        }));
        toast.success('¡Gracias por tu valoración!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al valorar');
        if (error.detail === 'Ya has valorado esta entrada') {
          setHasRated(true);
        }
      }
    } catch (error) {
      toast.error('Error al enviar la valoración');
    }
  };

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  const nextImage = useCallback(() => {
    if (post?.images && lightboxIndex < post.images.length - 1) {
      setLightboxIndex(prev => prev + 1);
    }
  }, [post?.images, lightboxIndex]);

  const prevImage = useCallback(() => {
    if (lightboxIndex > 0) {
      setLightboxIndex(prev => prev - 1);
    }
  }, [lightboxIndex]);

  const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const mapClass = theme === 'dark' ? 'leaflet-tiles-dark' : '';

  if (loading) {
    return (
      <main className="pt-24 pb-20 min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="h-96 bg-muted animate-pulse" />
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="pt-24 pb-20 min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl text-center">
          <h1 className="font-serif text-4xl mb-4">Artículo no encontrado</h1>
          <Link to="/#blog" className="text-muted-foreground hover:text-foreground">
            Volver al blog
          </Link>
        </div>
      </main>
    );
  }

  const readingTime = calculateReadingTime(post.content);

  return (
    <main className="pt-24 pb-20 min-h-screen bg-background" data-testid="blog-detail-page">
      <SEO 
        title={post.title}
        description={post.excerpt || `${post.content?.substring(0, 160)}...`}
        image={post.thumbnail || post.images?.[0]}
        url={`https://jonquintano.com/blog/${post.post_id}`}
        type="article"
        keywords={post.tags?.join(', ')}
        article={{
          publishedTime: post.date,
          author: 'Jon Quintano',
          category: post.category,
          tags: post.tags
        }}
      />
      {/* Lightbox */}
      {lightboxOpen && post.images && post.images.length > 0 && (
        <Lightbox
          images={post.images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}

      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        {/* Back Button */}
        <Link 
          to="/#blog" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al blog
        </Link>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main Content */}
          <article className="flex-1 min-w-0">
            {/* Featured Image - Full Width */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 -mx-4 md:mx-0"
            >
              <img
                src={post.thumbnail}
                alt={post.title}
                className="w-full aspect-[16/9] object-cover cursor-pointer hover:opacity-95 transition-opacity"
                onClick={() => post.images && post.images.length > 0 && openLightbox(0)}
              />
            </motion.div>

            {/* Post Header - Below Image (Flexblog Style) */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-10 text-center"
            >
              {/* Category */}
              <span className="inline-block px-4 py-1.5 bg-foreground text-background text-xs uppercase tracking-[0.2em] font-medium mb-6">
                {post.category}
              </span>
              
              {/* Title */}
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl xl:text-6xl tracking-tight mb-6 leading-tight">
                {post.title}
              </h1>
              
              {/* Meta Info */}
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {post.date}
                </span>
                <span className="text-muted-foreground/40">|</span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {post.location}
                </span>
                <span className="text-muted-foreground/40">|</span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {readingTime} min de lectura
                </span>
              </div>
              
              {/* Excerpt */}
              {post.excerpt && (
                <p className="text-lg text-muted-foreground mt-6 max-w-2xl mx-auto italic">
                  {post.excerpt}
                </p>
              )}

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  {post.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-muted text-xs text-muted-foreground">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </motion.header>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-none mb-8"
            >
              <p className="drop-cap text-lg text-muted-foreground leading-relaxed whitespace-pre-line">{post.content}</p>
            </motion.div>

            {/* Gallery Thumbnails - Navigation Buttons */}
            {post.images && post.images.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-8"
              >
                <h3 className="font-serif text-xl mb-4">Galería</h3>
                <div className="relative">
                  {/* Left Arrow */}
                  {galleryIndex > 0 && (
                    <button
                      onClick={() => setGalleryIndex(prev => Math.max(0, prev - getVisibleCount()))}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border flex items-center justify-center hover:bg-muted transition-colors"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}
                  
                  {/* Thumbnails Container */}
                  <div className="overflow-hidden mx-12">
                    <motion.div 
                      className="flex gap-3"
                      animate={{ x: -galleryIndex * (galleryItemWidth + 12) }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    >
                      {post.images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => openLightbox(index)}
                          className="flex-shrink-0 group relative overflow-hidden"
                          data-testid={`gallery-thumb-${index}`}
                        >
                          <img
                            src={image}
                            alt={`${post.title} - ${index + 1}`}
                            className="w-32 h-24 md:w-40 md:h-28 object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium transition-opacity">
                              Ver
                            </span>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  </div>
                  
                  {/* Right Arrow */}
                  {post.images.length > getVisibleCount() && galleryIndex < post.images.length - getVisibleCount() && (
                    <button
                      onClick={() => setGalleryIndex(prev => Math.min(post.images.length - getVisibleCount(), prev + getVisibleCount()))}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border flex items-center justify-center hover:bg-muted transition-colors"
                      data-testid="gallery-next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                </div>
                
                {/* Gallery indicator dots */}
                {post.images.length > getVisibleCount() && (
                  <div className="flex justify-center gap-2 mt-4">
                    {Array.from({ length: Math.ceil(post.images.length / getVisibleCount()) }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setGalleryIndex(idx * getVisibleCount())}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          Math.floor(galleryIndex / getVisibleCount()) === idx 
                            ? 'bg-foreground' 
                            : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                        }`}
                        data-testid={`gallery-dot-${idx}`}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Embedded Videos */}
            {post.videos && post.videos.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mb-8"
              >
                <h3 className="font-serif text-xl mb-4 flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Videos
                </h3>
                <div className="space-y-6">
                  {post.videos.map((video, index) => (
                    <div key={video.video_id || index} className="border border-border overflow-hidden">
                      <VideoEmbed video={video} />
                      {video.title && (
                        <div className="p-3 bg-muted/30">
                          <p className="text-sm font-medium">{video.title}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Location Map */}
            {post.coordinates && post.coordinates.length === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-8"
              >
                <h3 className="font-serif text-xl mb-4">Ubicación</h3>
                <div className="h-64 border border-border overflow-hidden">
                  <MapContainer
                    center={[post.coordinates[1], post.coordinates[0]]}
                    zoom={12}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                    className={mapClass}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url={tileUrl}
                    />
                    <Marker 
                      position={[post.coordinates[1], post.coordinates[0]]} 
                      icon={customIcon}
                    >
                      <Popup>{post.location}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </motion.div>
            )}

            {/* Mobile Share & Rating (visible on mobile) */}
            <div className="lg:hidden space-y-6 mb-8 p-6 bg-card border border-border">
              <div className="text-center">
                <h4 className="font-serif text-lg mb-4">¿Te ha gustado este artículo?</h4>
                <StarRating
                  rating={userRating || (post.rating_count > 0 ? post.rating_sum / post.rating_count : 0)}
                  onRate={handleRate}
                  disabled={hasRated}
                  totalRatings={post.rating_count || 0}
                />
              </div>
              <div className="flex justify-center">
                <ShareButtons url={window.location.href} title={post.title} />
              </div>
            </div>

            {/* Related Posts (Main Column on Desktop) */}
            <RelatedPosts posts={relatedPosts} />

            {/* Post Navigation */}
            <PostNavigation previous={navigation.previous} next={navigation.next} />
          </article>

          {/* Sidebar (Desktop Only) */}
          <div className="hidden lg:block">
            <div className="sticky top-28">
              <Sidebar
                post={post}
                relatedPosts={relatedPosts}
                onRate={handleRate}
                userRating={userRating}
                hasRated={hasRated}
                ratingBreakdown={post.rating_breakdown}
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default BlogDetail;
